import { useMutation } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectCreditCardStatisticsActiveVcnCount,
  selectCreditCardStatisticsVcnCount,
} from 'src/app/Account/redux/account.selectors'
import { selectCreditCardIdFromRoute } from 'src/app/AccountTransactions/redux/accountTransactions.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsFiltersBody,
  VirtualCardsListContext,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { useBackgroundDispatch } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { connectedReplace } from 'src/navigation/Lnk'
import { budgetVirtualCardsUrl } from 'src/navigation/urls'
import {
  useQueryCreditCard,
  useQueryCreditCardPermissions,
} from 'src/queries/creditCard.queries'
import { reportDownloadsMutations } from 'src/queries/reportDownloads/reportDownloads.queries'
import { createSetIsDownloadSuccessPopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

const AccountVirtualCards: React.FC = () => {
  const translate = useTranslate()
  const creditCardId = useSelector(selectCreditCardIdFromRoute)
  const { data: creditCard } = useQueryCreditCard({
    creditCardId: creditCardId,
  })
  const { data: creditCardPermissions } = useQueryCreditCardPermissions({
    creditCardId: creditCard?.id,
    enabled: !!creditCard?.id,
    retry: false,
  })

  const activeVcnCount = useSelector(selectCreditCardStatisticsActiveVcnCount)
  const vcnCount = useSelector(selectCreditCardStatisticsVcnCount)

  const currentUser = useCurrentUser()

  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 130)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 130)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()
  const reportVirtualCardsDownloadMutation = useMutation(
    reportDownloadsMutations.virtualCards(),
  )
  const dispatchPostReportVirtualCardsDownload = (
    filters: VirtualCardsFiltersBody,
  ): void => {
    reportVirtualCardsDownloadMutation.mutate(filters, {
      onSuccess: () => {
        dispatch(createSetIsDownloadSuccessPopupOpen(true))
      },
    })
  }

  const canUpdateCreditCard = creditCardPermissions?.canUpdateCreditCard

  // redirect to budget virtual cards if budget
  useEffect(() => {
    if (creditCard?.isBudget) {
      dispatch(connectedReplace(budgetVirtualCardsUrl(creditCard.id)))
    }
  }, [creditCard])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('account virtual cards filter applied', {
        card_user:
          selectedFilters.card_user && selectedFilters.card_user.length > 0,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        deactivating_in: !!selectedFilters.deactivating_in,
      })
    }
  }, [selectedFilters])

  return (
    <div
      css={css`
        height: 100%;
      `}
      ref={ref}
      id={'ref'}
    >
      <BackgroundPageContent noStackingIndex>
        <VirtualCardsBoxAndLinks
          context={VirtualCardsListContext.Account}
          creditCardId={creditCard?.id}
          onDownloadClick={
            canUpdateCreditCard
              ? dispatchPostReportVirtualCardsDownload
              : undefined
          }
          downloadPending={reportVirtualCardsDownloadMutation.isPending}
          defaultTitle={
            <ObjectBoxTitle
              title={translate('Virtual Cards Created')}
              static={{
                label: translate('Active'),
                count: activeVcnCount || 0,
              }}
            />
          }
          emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
          totalCount={vcnCount || 0}
          readOnly={
            creditCard?.ownerId !== currentUser?.id || creditCard?.isClosed
          }
          setSelectedFilters={setSelectedFilters}
          clientHeight={clientHeight || 0}
        />
      </BackgroundPageContent>
    </div>
  )
}

export default AccountVirtualCards
